<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">

      <!-- Filtros -->
      <div class="mb-3 d-flex align-items-end flex-wrap gap-2">

        <!-- Búsqueda -->
        <div>
          <label class="fs-12 text-muted d-block mb-1">Comprobante</label>
          <input
            v-model="search"
            @input="onSearch"
            type="text"
            class="form-control"
            placeholder="Ej: B001-0000123"
            style="min-width:200px"
          />
        </div>

        <!-- Sede -->
        <div>
          <label class="fs-12 text-muted d-block mb-1">Sede</label>
          <select v-model="idalmacen" @change="onFiltroChange" class="form-select" style="min-width:160px">
            <option :value="null">Todas las sedes</option>
            <option :value="1">Lapoint 939</option>
            <option :value="15">Lapoint 1179</option>
          </select>
        </div>

        <!-- Periodo rápido -->
        <div>
          <label class="fs-12 text-muted d-block mb-1">Período</label>
          <div class="btn-group">
            <button v-for="p in periodos" :key="p.key"
              class="btn btn-sm"
              :class="periodoActivo === p.key ? 'btn-purple' : 'btn-outline-secondary'"
              @click="setPeriodo(p.key)">{{ p.label }}</button>
          </div>
        </div>

        <!-- Rango manual (solo si periodo = 'rango') -->
        <template v-if="periodoActivo === 'rango'">
          <div>
            <label class="fs-12 text-muted d-block mb-1">Desde</label>
            <input type="date" v-model="fechaDesde" @change="onFiltroChange" class="form-control" />
          </div>
          <div>
            <label class="fs-12 text-muted d-block mb-1">Hasta</label>
            <input type="date" v-model="fechaHasta" @change="onFiltroChange" class="form-control" />
          </div>
        </template>

        <!-- Estado -->
        <div>
          <label class="fs-12 text-muted d-block mb-1">Estado</label>
          <select v-model="filtroEstado" @change="onFiltroChange" class="form-select" style="min-width:130px">
            <option value="todas">Todas</option>
            <option value="vigentes">Solo vigentes</option>
            <option value="anuladas">Solo anuladas</option>
          </select>
        </div>

        <div class="ms-auto d-flex align-items-end gap-2">
          <span v-if="loading" class="text-muted fs-13 pb-1">Cargando...</span>
          <span v-else class="text-muted fs-13 pb-1">{{ pagination.total }} ventas</span>
          <button class="btn btn-sm btn-outline-secondary" @click="limpiarFiltros">Limpiar</button>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger py-2 fs-14">{{ error }}</div>

      <div class="table-responsive style-three">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th class="text-title fw-normal fs-14 pt-0 ps-0">FECHA</th>
              <th class="text-title fw-normal fs-14 pt-0">COMPROBANTE</th>
              <th class="text-title fw-normal fs-14 pt-0">SEDE</th>
              <th class="text-title fw-normal fs-14 pt-0">VENDEDOR</th>
              <th class="text-title fw-normal fs-14 pt-0">CLIENTE</th>
              <th class="text-title fw-normal fs-14 pt-0 text-end">TOTAL</th>
              <th class="text-title fw-normal fs-14 pt-0 pe-0 col-acciones text-end">ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="n in 8" :key="n">
                <td colspan="7">
                  <div class="placeholder-glow">
                    <span class="placeholder col-12 rounded-1" style="height:20px"></span>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-else-if="!ventasFiltradas.length">
              <td colspan="7" class="text-center text-muted py-4 fs-14">No se encontraron ventas.</td>
            </tr>

            <tr v-else v-for="v in ventasFiltradas" :key="v.comprobante"
                :class="{ 'fila-anulada': v.anulado }">
              <td class="shadow-none fs-14 fw-normal ps-0" :class="v.anulado ? 'text-danger' : 'text-optional'">
                {{ formatFecha(v.fecha) }}
                <div v-if="v.hora_registro" class="text-muted" style="font-size:11px;line-height:1.2">
                  {{ formatHora(v.hora_registro) }}
                </div>
              </td>
              <td class="shadow-none fs-14 fw-semibold" :class="v.anulado ? 'text-danger' : 'text-paragraph'">
                <span class="d-inline-flex align-items-center gap-2">
                  <span class="badge" :class="v.tipo_doc === 'F' ? 'bg-warning text-dark' : 'bg-primary'"
                    :title="v.tipo_doc === 'F' ? 'Factura' : 'Boleta'"
                    style="font-size:10px;min-width:18px">{{ v.tipo_doc || '?' }}</span>
                  <span>{{ v.comprobante }}</span>
                  <span v-if="v.anulado" class="badge bg-danger fw-normal" style="font-size:10px">ANULADA</span>
                </span>
              </td>
              <td class="shadow-none">
                <span class="sede-pill">{{ sedeCorta(v) }}</span>
              </td>
              <td class="shadow-none fs-14 fw-normal text-paragraph">
                <span v-if="v.vendedor" class="vendedor-chip">{{ v.vendedor }}</span>
                <span v-else class="text-muted fs-12">—</span>
              </td>
              <td class="shadow-none fs-14 fw-normal text-paragraph">
                <template v-if="clienteDisplay(v)">
                  <div>{{ clienteDisplay(v) }}</div>
                  <div v-if="v.cliente_doc && v.cliente_doc !== '00000000'" class="text-muted" style="font-size:11px">{{ v.cliente_doc }}</div>
                </template>
                <span v-else class="text-muted fs-12">—</span>
              </td>
              <td class="shadow-none fs-14 fw-semibold text-end" :class="v.anulado ? 'text-danger text-decoration-line-through' : 'text-paragraph'">
                S/ {{ Number(v.total).toFixed(2) }}
                <div class="text-muted fw-normal" style="font-size:11px;line-height:1.2">
                  {{ v.items }} {{ Number(v.items) === 1 ? 'ítem' : 'ítems' }}
                </div>
              </td>
              <td class="shadow-none lh-1 text-end pe-0 col-acciones">
                <div class="button-group d-flex flex-wrap align-items-center gap-2 justify-content-end">
                  <!-- Estado / acción SUNAT (solo ventas del sistema nuevo, con idcta) -->
                  <span v-if="v.idcta && v.emitido_sunat" title="Emitido a SUNAT"
                    class="badge bg-success d-flex align-items-center" style="font-size:11px">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2 2 4-4" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                  <button v-else-if="v.idcta && !v.anulado"
                    class="btn btn-sm btn-outline-primary py-0 px-2"
                    style="font-size:11px"
                    title="Emitir a SUNAT"
                    :disabled="emitiendo === v.idcta"
                    @click="emitirSunat(v)">
                    <span v-if="emitiendo === v.idcta" class="spinner-border spinner-border-sm me-1" style="width:10px;height:10px"></span>
                    {{ emitiendo === v.idcta ? '...' : 'Emitir' }}
                  </button>

                  <a href="javascript:void(0)" title="Ver detalle"
                    data-bs-toggle="modal" data-bs-target="#detailsModal"
                    @click="$emit('ver-detalle', v.comprobante)">
                    <img src="../../../assets/img/icons/eye.svg" alt="Ver" />
                  </a>
                  <a v-if="!v.anulado"
                    href="javascript:void(0)" title="Anular venta" class="text-danger"
                    @click="confirmarAnular(v)">
                    <img src="../../../assets/img/icons/close-circle-2.svg" alt="Anular" style="width:18px" />
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div class="row pb-45 align-items-center" v-if="pagination.total > 0">
    <div class="col-sm-6">
      <div class="d-flex align-items-center gap-2 justify-content-center justify-content-sm-start page-unit">
        <span class="fs-13">Por página</span>
        <select class="text-title border-0 fs-14 bg-transparent" v-model="pageSize" @change="onPageSizeChange">
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
        </select>
      </div>
    </div>
    <div class="col-sm-6 text-sm-end text-center">
      <ul class="page-nav list-style">
        <li>
          <a href="#" @click.prevent="prevPage">
            <img src="../../../assets/img/icons/left-arrow-purple.svg" alt="Anterior" />
          </a>
        </li>
        <li v-for="p in visiblePages" :key="p">
          <a href="#" @click.prevent="goToPage(p)" :class="{ active: p === currentPage }">{{ p }}</a>
        </li>
        <li>
          <a href="#" @click.prevent="nextPage">
            <img src="../../../assets/img/icons/right-arrow-purple.svg" alt="Siguiente" />
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import api from '../../../services/api';
import { getUser } from '../../../services/authService';

// Fecha en formato YYYY-MM-DD usando la hora LOCAL (no UTC).
// toISOString() convierte a UTC → en Lima (UTC-5) por la tarde/noche da el día
// siguiente y el filtro "Hoy" no encontraba ventas. Ver CLAUDE.md (bug fechas UTC).
const fmtLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const hoy = () => fmtLocal(new Date());
const restarDias = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return fmtLocal(d);
};

export default {
  name: 'SalesList',
  emits: ['ver-detalle', 'recrear-venta'],
  data() {
    const user = getUser();
    const idalmacenFijo = user?.idalmacen || null;
    return {
      ventas: [],
      pagination: { total: 0, limit: 25, offset: 0, pages: 0 },
      loading: false,
      error: null,
      search: '',
      idalmacen: idalmacenFijo,
      idalmacenFijo,
      fechaDesde: hoy(),
      fechaHasta: hoy(),
      periodoActivo: 'hoy',
      filtroEstado: 'todas',
      currentPage: 1,
      pageSize: 25,
      searchTimer: null,
      emitiendo: null,
      periodos: [
        { key: 'hoy',    label: 'Hoy' },
        { key: '7d',     label: '7 días' },
        { key: '30d',    label: '30 días' },
        { key: 'mes',    label: 'Este mes' },
        { key: 'todo',   label: 'Todo' },
        { key: 'rango',  label: 'Rango' },
      ],
    };
  },
  computed: {
    totalPages() { return Math.ceil(this.pagination.total / this.pageSize) || 1; },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end   = Math.min(this.totalPages, this.currentPage + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    ventasFiltradas() {
      if (this.filtroEstado === 'vigentes') return this.ventas.filter(v => !v.anulado);
      if (this.filtroEstado === 'anuladas') return this.ventas.filter(v => v.anulado);
      return this.ventas;
    },
  },
  mounted() { this.cargar(); },
  methods: {
    setPeriodo(key) {
      this.periodoActivo = key;
      const hoyStr = hoy();
      if (key === 'hoy')  { this.fechaDesde = hoyStr; this.fechaHasta = hoyStr; }
      else if (key === '7d')  { this.fechaDesde = restarDias(6); this.fechaHasta = hoyStr; }
      else if (key === '30d') { this.fechaDesde = restarDias(29); this.fechaHasta = hoyStr; }
      else if (key === 'mes') {
        const d = new Date(); d.setDate(1);
        this.fechaDesde = fmtLocal(d); this.fechaHasta = hoyStr;
      }
      else if (key === 'todo') { this.fechaDesde = null; this.fechaHasta = null; }
      this.currentPage = 1;
      this.cargar();
    },
    async cargar() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          limit:      this.pageSize,
          offset:     (this.currentPage - 1) * this.pageSize,
          search:     this.search || undefined,
          idalmacen:  this.idalmacen || undefined,
          fechaDesde: this.periodoActivo !== 'todo' ? (this.fechaDesde || undefined) : undefined,
          fechaHasta: this.periodoActivo !== 'todo' ? (this.fechaHasta || undefined) : undefined,
        };
        const res = await api.get('/ventas', { params });
        this.ventas     = res.data.data || [];
        this.pagination = res.data.pagination || { total: res.data.total || 0, limit: this.pageSize, offset: 0 };
      } catch (e) {
        this.error = e.response?.data?.error || 'Error al cargar las ventas.';
      } finally {
        this.loading = false;
      }
    },
    async confirmarAnular(venta) {
      if (!confirm(`¿Anular la venta ${venta.comprobante}?\nSe revertirá el stock. La venta quedará registrada como ANULADA.`)) return;
      try {
        await api.delete(`/ventas/${encodeURIComponent(venta.comprobante)}`);
        this.cargar();
      } catch (e) {
        alert(e.response?.data?.error || 'Error al anular la venta');
      }
    },
    limpiarFiltros() {
      this.search = ''; this.idalmacen = this.idalmacenFijo; this.filtroEstado = 'todas';
      this.setPeriodo('hoy');
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.currentPage = 1; this.cargar(); }, 350);
    },
    onFiltroChange() { this.currentPage = 1; this.cargar(); },
    onPageSizeChange() { this.currentPage = 1; this.cargar(); },
    goToPage(p) { if (p < 1 || p > this.totalPages) return; this.currentPage = p; this.cargar(); },
    prevPage() { this.goToPage(this.currentPage - 1); },
    nextPage() { this.goToPage(this.currentPage + 1); },
    clienteDisplay(v) {
      const c = (v.cliente || '').trim();
      if (!c || c === '-') return null;
      const genericos = ['VARIOS', 'OTROS', 'CLIENTES VARIOS'];
      if (genericos.some(g => c.toUpperCase().includes(g))) return null;
      return c;
    },
    async emitirSunat(venta) {
      if (!confirm(`¿Emitir ${venta.comprobante} a SUNAT vía Nubefact?`)) return;
      this.emitiendo = venta.idcta;
      try {
        const res = await api.post(`/facturacion/${venta.idcta}/emitir`);
        if (res.data.aceptado) {
          alert(`✓ Comprobante aceptado por SUNAT\n${res.data.mensaje || ''}`);
        } else {
          alert(`Enviado pero con observación:\n${res.data.mensaje || ''}`);
        }
        this.cargar();
      } catch (e) {
        alert(e.response?.data?.error || 'Error al emitir a SUNAT');
      } finally {
        this.emitiendo = null;
      }
    },
    formatFecha(fecha) {
      if (!fecha) return '—';
      const s = String(fecha).substring(0, 10);
      return new Date(s + 'T00:00:00').toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatHora(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    // Etiqueta corta de sede para el pill. idalmacen 1 → 939, 15 → 1179 (ver CLAUDE.md).
    sedeCorta(v) {
      if (v.almacenid === 1) return '939';
      if (v.almacenid === 15) return '1179';
      // Fallback: quitar el prefijo genérico "TIENDA A.LAPOINT" y el punto final
      return (v.almacen || '—').replace(/TIENDA\s+A\.?LAPOINT\s*/i, '').replace(/\.$/, '').trim() || '—';
    },
  },
};
</script>

<style scoped>
.fila-anulada { background: #fff5f5; opacity: 0.75; }
.fila-anulada:hover { background: #fee2e2; }
.vendedor-chip {
  display: inline-block; padding: 2px 8px;
  background: #eff6ff; color: #2563eb;
  border-radius: 4px; font-size: 12px; font-weight: 500;
}
/* Pill de sede: compacto y neutro, reemplaza el nombre largo del almacén */
.sede-pill {
  display: inline-block; padding: 2px 10px;
  background: #f1f5f9; color: #475569;
  border-radius: 6px; font-size: 12px; font-weight: 600;
}
.btn-purple { background: #7c3aed; color: white; border-color: #7c3aed; }
.btn-purple:hover { background: #6d28d9; }

/* Filas con un poco más de aire y hover para escanear mejor */
tbody td { padding-top: 12px; padding-bottom: 12px; }
tbody tr:not(.fila-anulada):hover { background: #f8fafc; }
tbody tr:not(.fila-anulada):hover .col-acciones { background: #f8fafc; }

/* Columna ACCIONES fijada a la derecha: en monitores chicos la tabla hace scroll
   horizontal pero el botón de acciones queda siempre visible pegado al borde. */
.col-acciones {
  position: sticky;
  right: 0;
  background: #fff;
  /* sombra sutil a la izquierda para que se lea como columna "flotante" sobre el contenido */
  box-shadow: -6px 0 6px -6px rgba(0, 0, 0, 0.15);
}
thead .col-acciones { z-index: 2; }
/* mantener el tinte de las filas anuladas también en la celda fija */
.fila-anulada .col-acciones { background: #fff5f5; }
.fila-anulada:hover .col-acciones { background: #fee2e2; }
</style>
